import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Text,
  TokenTextAppearance,
  styled,
  TextTag,
  DisplayTag,
} from '@volkswagen-onehub/components-core';
import { GiveawayContext } from '@/state/state';
import { minimalScreenWidth } from '@/common/breakpoints';
import { isOrderPrice } from '@/util/currency';
import { Consumption } from '@/components/consumption/consumption';
import { PriceCard } from './priceCard';

export interface YourCarProps {
  requiresSmallerBottomSpacing: boolean;
}

export interface ModelOverviewProps {
  withConsumptionLayer: boolean;
}

const StyledOverview = styled.div<{ $hasPricing: boolean }>`
  display: ${(props) => (props.$hasPricing ? 'grid' : 'block')};
  grid-template-columns: 1fr;
  grid-template-areas: ${(props) =>
    props.$hasPricing ? `'model' 'wltp' 'price' 'date'` : `'model' 'wltp' 'date'`};
  row-gap: ${(props) => props.theme.size.static150};
  align-items: start;
  padding-left: ${(props) => props.theme.size.dynamic0150};
  padding-right: ${(props) => props.theme.size.dynamic0150};
  padding-top: ${(props) => props.theme.size.dynamic0150};
  padding-bottom: ${(props) => props.theme.size.dynamic0050};
  overflow: visible;

  @media screen and ${minimalScreenWidth.l} {
    padding-left: ${(props) => props.theme.size.dynamic0250};
    padding-right: ${(props) => props.theme.size.dynamic0250};
    grid-template-columns: ${(props) =>
      props.$hasPricing ? 'minmax(0, 1fr) minmax(9rem, 17.5rem)' : '1fr'};
    grid-template-areas: ${(props) =>
      props.$hasPricing ? `'model price' 'wltp wltp' 'date date'` : `'model' 'wltp' 'date'`};
    column-gap: ${(props) => props.theme.size.dynamic0250};
  }

  @media screen and ${minimalScreenWidth.xl} {
    padding-left: ${(props) => props.theme.size.dynamic0450};
    padding-right: ${(props) => props.theme.size.dynamic0450};
  }
`;

const StyledSection = styled.section`
  grid-area: model;
  min-width: 0;
`;

const StyledTextContainer = styled.div<YourCarProps>`
  padding-bottom: ${(props) =>
    props.requiresSmallerBottomSpacing ? props.theme.size.dynamic0050 : 0};
`;

const StyledMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${(props) => props.theme.size.static250};
  margin-top: ${(props) => props.theme.size.static250};
`;

const StyledDisplayTag = styled(DisplayTag)`
  padding: ${(props) => props.theme.size.static250};
`;

const StyledModelText = styled(Text)`
  max-width: 580px;
  width: 100%;
  overflow-wrap: normal;
  word-break: normal;
  hyphens: none;

  @media screen and ${minimalScreenWidth.l} {
    max-width: none;
  }
`;

export const YourCar = (props: YourCarProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const { carModel, marketingCode } = useContext(GiveawayContext);
  const { requiresSmallerBottomSpacing } = props;
  const isVIN = marketingCode === 'VIN';

  if (!carModel) {
    return null;
  }

  return (
    <StyledSection data-testid="yourcar-container">
      <StyledTextContainer
        requiresSmallerBottomSpacing={requiresSmallerBottomSpacing}
        data-testid="yourcar-text-container"
      >
        <Text tag={TextTag.h2} bold appearance={TokenTextAppearance.headline400}>
          {t('header.model')}
        </Text>

        <StyledModelText appearance={TokenTextAppearance.headline400}>{carModel}</StyledModelText>

        {!isVIN && (
          <StyledMetaRow>
            <StyledDisplayTag
              appearance="info"
              text={`${t('sideBar.marketingCode')}: ${marketingCode}`}
            />
          </StyledMetaRow>
        )}
      </StyledTextContainer>
    </StyledSection>
  );
};

export const ModelOverview = ({ withConsumptionLayer }: ModelOverviewProps): React.ReactElement => {
  const { pricing } = useContext(GiveawayContext);
  const hasPricing = isOrderPrice(pricing);

  return (
    <StyledOverview $hasPricing={hasPricing} data-testid="model-overview">
      <YourCar requiresSmallerBottomSpacing={withConsumptionLayer} />
      {hasPricing && <PriceCard />}
      {withConsumptionLayer && <Consumption splitLayout={hasPricing} />}
    </StyledOverview>
  );
};
